// chats-layer/services/chats.service.ts
import { BaseService } from '../../../common/http-layer/service/base.service'
import type {
  IConversationApi,
  IConversationPaginationParams,
  IConversationSuggestion,
  IConversationSuggestionsResponse,
  ICreateConversationInput,
  IMessage,
  ISendMessageInput,
  ISuggestionPaginationParams,
} from '../types/conversation.types'

export class ChatsService extends BaseService {
  constructor() {
    super({
      baseURL: 'COMMUNITIES_SERVER_URL',
      basePath: '',
    })
  }

  /**
   * Build endpoint path with locationId
   */
  private endpoint(path: string = '', locationId?: string): string {
    if (!locationId) {
      throw new Error('Location ID is required for chat operations')
    }
    return `/${locationId}/conversations${path}`
  }

  /**
   * Fetch paginated list of conversations
   */
  async getConversations(
    locationId: string,
    params?: IConversationPaginationParams
  ): Promise<{ conversations: IConversationApi[]; hasMore: boolean; nextCursor?: string }> {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.lastMessageAtCursor) queryParams.append('lastMessageAtCursor', params.lastMessageAtCursor)
    if (params?.unread) queryParams.append('unread', 'true')

    const queryString = queryParams.toString()
    const endpoint = this.endpoint(queryString ? `?${queryString}` : '', locationId)

    const { data } = await this.get<IConversationApi[]>(endpoint)

    return {
      conversations: data || [],
      hasMore: (data?.length || 0) >= (params?.limit || 10),
      nextCursor: data?.length ? data[data.length - 1].lastMessageAt : undefined,
    }
  }

  /**
   * Get a single conversation by ID
   */
  async getConversationById(locationId: string, conversationId: string): Promise<IConversationApi> {
    const { data } = await this.get<IConversationApi>(this.endpoint(`/${conversationId}`, locationId))
    return data
  }

  /**
   * Get messages for a conversation (paginated by lastCreatedAt)
   */
  async getMessages(
    locationId: string,
    conversationId: string,
    lastCreatedAt?: string,
    limit: number = 20
  ): Promise<{ messages: IMessage[]; hasMore: boolean; nextCursor?: string }> {
    const queryParams = new URLSearchParams()
    queryParams.append('limit', limit.toString())
    queryParams.append('sort', 'descending')
    if (lastCreatedAt) queryParams.append('lastCreatedAt', lastCreatedAt)

    const { data } = await this.get<IMessage[]>(this.endpoint(`/${conversationId}/messages?${queryParams.toString()}`, locationId))

    return {
      messages: data || [],
      hasMore: (data?.length || 0) >= limit,
      nextCursor: data?.length ? data[data.length - 1].createdAt : undefined,
    }
  }

  /**
   * Create a new conversation
   */
  async createConversation(locationId: string, input: ICreateConversationInput, groupId?: string): Promise<IConversationApi> {
    const queryParams = new URLSearchParams()
    if (input.skipFetchingContactId) {
      queryParams.append('skipFetchingContactId', 'true')
    }
    const queryString = queryParams.toString()
    const endpoint = this.endpoint(queryString ? `?${queryString}` : '', locationId)

    const payload: {
      members: string[]
      type: ICreateConversationInput['type']
      initialMessage?: string
    } = {
      members: input.members,
      type: input.type,
    }

    if (input.initialMessage !== undefined) {
      payload.initialMessage = input.initialMessage
    }

    const { data } = await this.post<IConversationApi>(endpoint, payload, { locationId, groupId })
    return data
  }

  /**
   * Send a message to a conversation
   * NOTE: This is a placeholder - actual message sending is done via sockets
   * @see socket layer for real-time messaging implementation
   */
  async sendMessage(_locationId: string, _input: ISendMessageInput) {
    // Placeholder
  }

  /**
   * Mark a conversation as read
   */
  async markAsRead(locationId: string, conversationId: string): Promise<void> {
    await this.post(this.endpoint(`/${conversationId}/read`, locationId))
  }

  /**
   * Block a conversation
   */
  async blockConversation(locationId: string, conversationId: string): Promise<void> {
    await this.post(this.endpoint(`/${conversationId}/block`, locationId))
  }

  /**
   * Unblock a conversation
   */
  async unblockConversation(locationId: string, conversationId: string): Promise<void> {
    await this.post(this.endpoint(`/${conversationId}/unblock`, locationId))
  }

  /**
   * Delete conversation history
   */
  async deleteConversation(locationId: string, conversationId: string): Promise<void> {
    await this.delete(this.endpoint(`/${conversationId}`, locationId))
  }

  /**
   * Get conversation suggestions (search members to start a new chat)
   */
  async getConversationSuggestions(locationId: string, params?: ISuggestionPaginationParams): Promise<IConversationSuggestionsResponse> {
    const queryParams = new URLSearchParams()
    if (params?.searchText) queryParams.append('searchText', params.searchText)
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.token) queryParams.append('token', params.token)

    const queryString = queryParams.toString()
    const endpoint = this.endpoint(`/suggestions${queryString ? `?${queryString}` : ''}`, locationId)

    const { data } = await this.get<IConversationSuggestion[]>(endpoint)

    // Transform to paginated response
    const suggestions = data || []
    return {
      suggestions,
      hasMore: suggestions.length >= (params?.limit || 10),
      nextToken: suggestions.length ? suggestions[suggestions.length - 1].token : undefined,
    }
  }
}
